/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Tooltip from '@welldev-group/rcl-tooltip';
import { v4 as uuidv4 } from 'uuid';
import httpClient from '../../../shared/libraries/httpClient';

const CustomToolbar = ({ error, toolbarId }) => (
  <div id={toolbarId}>
    <button className="ql-insertAttachment" type="button">
      <Icon name="attachFile" />
    </button>
    <button className="ql-bold" type="button" />
    <button className="ql-italic" type="button" />
    <button className="ql-strike" type="button" />
    <button className="ql-list" value="ordered" type="button" />
    <button className="ql-list" value="bullet" type="button" />
    <span className="ql-toolbar-error col-grid col-bleed direction-row justify-end">
      {error && (
        <Tooltip
          className="texeditor-ql-toolbar-error-tooltip"
          content={error}
          position="bottom-right"
          type="danger"
          size="small"
        >
          <Icon name="invalid" color="danger" />
        </Tooltip>
      )}
    </span>
  </div>
);

CustomToolbar.propTypes = {
  error: PropTypes.string,
  toolbarId: PropTypes.string.isRequired,
};

CustomToolbar.defaultProps = {
  error: null,
};

const isTextFile = fileName =>
  ['pdf'].indexOf(
    fileName
      .split('.')
      .pop()
      .toLowerCase(),
  ) > -1;

const TextEditor = props => {
  const reactQuillRef = useRef();

  const handleOnChange = (html, _delta, source) => {
    if (source === 'user') {
      props.onChange(html);
    }
  };

  const insertAttachment = () => {
    const quillRef = reactQuillRef.current.getEditor();
    const range = quillRef.getSelection();

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*, application/pdf');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];

      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_name', file.name);

      const { data } = await httpClient.post('/attachments', formData);
      props.onAttachmentUpload(data.id);

      if (isTextFile(data.fileName)) {
        quillRef.insertText(
          range.index,
          data.fileName,
          'link',
          data.url,
          'user',
        );
        // Move cursor to right side of file name (easier to continue typing)
        quillRef.setSelection(range.index + data.fileNameLength);
      } else {
        quillRef.insertEmbed(range.index, 'image', data.url, 'user');
        // Move cursor to right side of image (easier to continue typing)
        quillRef.setSelection(range.index + 1);
      }
    };
  };

  const toolbarId = useMemo(() => `toolbar-${uuidv4()}`, []);

  const toolbarOptions = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          insertAttachment,
        },
      },
    }),
    [],
  );

  return (
    <div className="text-editor">
      <CustomToolbar error={props.error} toolbarId={toolbarId} />
      <ReactQuill
        ref={reactQuillRef}
        theme="snow"
        modules={toolbarOptions}
        value={props.value}
        onChange={handleOnChange}
        placeholder={props.placeholder}
      />
    </div>
  );
};

TextEditor.defaultProps = {
  onAttachmentUpload: () => {},
};

TextEditor.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onAttachmentUpload: PropTypes.func,
  error: PropTypes.string,
};

TextEditor.defaultProps = {
  value: '',
  placeholder: '',
  error: null,
};

export default TextEditor;
